import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/services/api'

interface User {
  id: string
  email: string
  name: string
  role: string
  organizationId?: string
  organization_id?: string
  email_verified?: boolean
  billing?: {
    requires_payment_setup?: boolean
    trial_active?: boolean
    days_left_in_trial?: number
    billing_status?: string
    payment_method_on_file?: boolean
  }
}

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  register: (
    email: string,
    password: string,
    name: string,
    organizationName: string
  ) => Promise<{ requires_verification?: boolean; email?: string; message?: string }>
  verifyEmail: (token: string) => Promise<{ requires_payment_setup?: boolean }>
  logout: () => void
  checkAuth: () => Promise<void>
}

/**
 * Only the token is persisted, so `user` is always null on a fresh page load
 * and has to be re-fetched by `checkAuth`. Start in the loading state whenever
 * a token is present, otherwise `ProtectedRoute` sees `user === null` on the
 * very first render and redirects to /login before `checkAuth` can resolve —
 * which would bounce every hard refresh and deep link back to the login page.
 */
const hasStoredToken = (): boolean => {
  try {
    return Boolean(localStorage.getItem('token'))
  } catch {
    return false
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: hasStoredToken(),
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/auth/login', { email, password })
          const { user, token } = response.data
          localStorage.setItem('token', token)
          try {
            const me = await api.get('/auth/me')
            set({ user: me.data, token, isLoading: false })
          } catch {
            set({ user, token, isLoading: false })
          }
        } catch (error: any) {
          const payload = error.response?.data
          const message =
            (payload && typeof payload === 'object' && payload.message) ||
            (typeof payload === 'string' ? payload : null) ||
            'Login failed'
          set({ error: message, isLoading: false })
          throw Object.assign(error, { displayMessage: message })
        }
      },

      register: async (email: string, password: string, name: string, organizationName: string) => {
        set({ isLoading: true, error: null })
        try {
          // Split the full name into first and last (last word is last name, rest is first)
          const nameParts = name.trim().split(' ')
          const firstName = nameParts[0]
          const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''
          const response = await api.post('/auth/register', {
            email,
            password,
            first_name: firstName,
            last_name: lastName,
            role: 'owner',
            organization_name: organizationName,
          })
          if (response.data?.requires_verification) {
            set({ isLoading: false })
            return {
              requires_verification: true,
              email: response.data.email,
              message: response.data.message,
            }
          }
          const { user, token } = response.data
          set({ user, token, isLoading: false })
          localStorage.setItem('token', token)
          return {}
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Registration failed', 
            isLoading: false 
          })
          throw error
        }
      },

      verifyEmail: async (token: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/auth/verify-email', { token })
          const { user, token: jwt } = response.data
          set({ user, token: jwt, isLoading: false })
          localStorage.setItem('token', jwt)
          return {
            requires_payment_setup: Boolean(response.data?.billing?.requires_payment_setup),
          }
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Verification failed',
            isLoading: false,
          })
          throw error
        }
      },

      logout: () => {
        set({ user: null, token: null })
        localStorage.removeItem('token')
      },

      checkAuth: async () => {
        const token = get().token || localStorage.getItem('token')
        if (!token) {
          // Clear isLoading too: the initial state may have optimistically set
          // it from a stored token that has since been removed.
          set({ user: null, token: null, isLoading: false })
          return
        }

        set({ isLoading: true })
        try {
          const response = await api.get('/auth/me')
          set({ user: response.data, token, isLoading: false })
        } catch {
          set({ user: null, token: null, isLoading: false })
          localStorage.removeItem('token')
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ token: state.token }),
    }
  )
)